"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, Sparkles, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = React.useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ABrighterViewForYourHome() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="39599" id="instant-cash-solution" className="relative w-full overflow-hidden bg-background py-20 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="animate-pulse absolute -top-[20%] -left-[10%] h-[600px] w-[600px] rounded-full bg-primary/5 blur-3xl duration-[4000ms]" />
        <div className="animate-pulse absolute top-[40%] -right-[10%] h-[500px] w-[500px] rounded-full bg-secondary/10 blur-3xl delay-1000 duration-[5000ms]" />
        <div className="animate-pulse absolute -bottom-[10%] left-[20%] h-[400px] w-[400px] rounded-full bg-muted/50 blur-3xl delay-2000 duration-[6000ms]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-8">
          {}
          <div ref={textRef} className={`lg:col-span-6 flex flex-col gap-8 transition-all duration-1000 ease-out ${textVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-sm border border-border bg-muted px-4 py-1.5 text-xs font-mono font-bold uppercase tracking-widest text-foreground backdrop-blur-sm">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Trusted Local Cash Buyers</span>
              </div>

              <h1 className="text-5xl font-extrabold tracking-tighter text-foreground sm:text-6xl lg:text-8xl xl:text-8xl">
                We Buy Houses{" "}
                <span className="text-primary">
                  Fast
                </span>{" "}
                for Cash
              </h1>

              <p className="max-w-2xl text-lg text-muted-foreground sm:text-xl leading-relaxed font-medium">Skip the repairs, commissions, and months of uncertainty. New Chapter Property Solutions provides a guaranteed cash offer for your home in any condition, closing on your timeline.</p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-14 px-8 text-lg font-bold rounded-sm shadow-xl transition-all duration-300 bg-primary hover:bg-foreground hover:text-primary hover:scale-105">
                <Link to="#">
                  Get Your Cash Offer
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              <div className="flex items-center gap-6 px-4 text-sm font-bold uppercase tracking-wide text-muted-foreground font-mono">
                <div className="flex items-center gap-2">
                  <CircleCheck className="h-5 w-5 text-primary" />
                  <span>No Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <CircleCheck className="h-5 w-5 text-primary" />
                  <span>Any Condition</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-6 relative mt-12 lg:mt-0 transition-all duration-1000 delay-300 ease-out ${imageVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}`}>
            <div className="relative mx-auto max-w-full lg:max-w-none">
              {}
              <div className="relative z-10 overflow-hidden rounded-sm border border-border bg-card shadow-2xl">
                <img src="https://newchapterpropertysolutions.com/wp-content/uploads/2026/04/IMG_9727.jpeg" alt="Modern editorial home exterior photograph" className="h-[500px] lg:h-[750px] w-full object-cover saturate-50 contrast-110 !m-0" />
                {}
                <div className="absolute inset-0 bg-gradient-to-tr from-foreground/20 to-transparent opacity-40 pointer-events-none" />
              </div>

              {}
              <div className="absolute -bottom-12 -left-8 z-20 w-[60%] sm:-left-12 sm:w-[55%] animate-pulse">
                
              </div>

              {}
              <div className="absolute -top-8 -right-8 -z-10 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
              <div className="absolute -bottom-8 -left-8 -z-10 h-64 w-64 rounded-full bg-secondary/10 blur-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}